import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import {
  type EnvironmentId,
  type ModelSelection,
  type ProviderInteractionMode,
  type ProjectId,
  RuntimeMode,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { truncate } from "@cmmd-forge/shared/String";
import { type LegendListRef } from "@legendapp/list/react";
import { useCallback, type Dispatch, type SetStateAction } from "react";
import { readEnvironmentApi } from "../../environmentApi";
import {
  buildPlanImplementationPrompt,
  buildPlanImplementationThreadTitle,
} from "../../proposedPlan";
import { type ChatMessage, type Thread } from "../../types";
import { formatOutgoingPrompt, waitForStartedServerThread } from "../ChatView.logic";
import { toastManager } from "../ui/toast";
import { newCommandId, newMessageId, newThreadId } from "~/lib/utils";
import { type ComposerModelSelection } from "./useComposerModelSelection";

export type PlanFollowUpInteractionMode = "default" | "plan";

export function usePlanFollowUpControls(input: {
  readonly environmentId: EnvironmentId;
  readonly routeThreadId: ThreadId;
  readonly activeThread: Thread | undefined;
  readonly activeProject:
    | {
        readonly id: ProjectId;
      }
    | null
    | undefined;
  readonly activeProposedPlan:
    | {
        readonly id: string;
        readonly planMarkdown: string;
      }
    | null
    | undefined;
  readonly activeThreadBranch: string | null;
  readonly isServerThread: boolean;
  readonly isSendBusy: boolean;
  readonly isConnecting: boolean;
  readonly sendInFlightRef: { current: boolean };
  readonly composerModelSelection: ComposerModelSelection;
  readonly runtimeMode: RuntimeMode;
  readonly beginLocalDispatch: (options?: { readonly preparingWorktree?: boolean }) => void;
  readonly resetLocalDispatch: () => void;
  readonly setThreadError: (targetThreadId: ThreadId | null, error: string | null) => void;
  readonly showScrollDebouncer: { current: { cancel: () => void } };
  readonly setShowScrollToBottom: (show: boolean) => void;
  readonly legendListRef: { current: LegendListRef | null };
  readonly isAtEndRef: { current: boolean };
  readonly setOptimisticUserMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  readonly persistThreadSettingsForNextTurn: (settings: {
    readonly threadId: ThreadId;
    readonly createdAt: string;
    readonly modelSelection?: ModelSelection;
    readonly runtimeMode: RuntimeMode;
    readonly interactionMode: ProviderInteractionMode;
  }) => Promise<void>;
  readonly setComposerDraftInteractionMode: (
    target: ScopedThreadRef,
    mode: ProviderInteractionMode,
  ) => void;
  readonly openPlanPanel: (threadId: ThreadId, environmentId: EnvironmentId) => Promise<void>;
}): {
  readonly onSubmitPlanFollowUp: (request: {
    readonly text: string;
    readonly interactionMode: PlanFollowUpInteractionMode;
  }) => Promise<void>;
  readonly onImplementPlanInNewThread: () => Promise<void>;
} {
  const onSubmitPlanFollowUp = useCallback(
    async ({
      text,
      interactionMode: nextInteractionMode,
    }: {
      readonly text: string;
      readonly interactionMode: PlanFollowUpInteractionMode;
    }) => {
      const api = readEnvironmentApi(input.environmentId);
      if (
        !api ||
        !input.activeThread ||
        !input.isServerThread ||
        input.isSendBusy ||
        input.isConnecting ||
        input.sendInFlightRef.current
      ) {
        return;
      }

      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }

      const {
        provider: selectedProvider,
        model: selectedModel,
        models: selectedProviderModels,
        promptEffort: selectedPromptEffort,
        modelSelection: selectedModelSelection,
      } = input.composerModelSelection;

      const threadIdForSend = input.activeThread.id;
      const messageIdForSend = newMessageId();
      const messageCreatedAt = new Date().toISOString();
      const outgoingMessageText = formatOutgoingPrompt({
        provider: selectedProvider,
        model: selectedModel,
        models: selectedProviderModels,
        effort: selectedPromptEffort,
        text: trimmed,
      });

      input.sendInFlightRef.current = true;
      input.beginLocalDispatch({ preparingWorktree: false });
      input.setThreadError(threadIdForSend, null);

      input.isAtEndRef.current = true;
      input.showScrollDebouncer.current.cancel();
      input.setShowScrollToBottom(false);
      await input.legendListRef.current?.scrollToEnd?.({ animated: false });

      input.setOptimisticUserMessages((existing) => [
        ...existing,
        {
          id: messageIdForSend,
          role: "user",
          text: outgoingMessageText,
          createdAt: messageCreatedAt,
          streaming: false,
        },
      ]);

      try {
        await input.persistThreadSettingsForNextTurn({
          threadId: threadIdForSend,
          createdAt: messageCreatedAt,
          modelSelection: selectedModelSelection,
          runtimeMode: input.runtimeMode,
          interactionMode: nextInteractionMode,
        });

        input.setComposerDraftInteractionMode(
          scopeThreadRef(input.activeThread.environmentId, threadIdForSend),
          nextInteractionMode,
        );

        await api.orchestration.dispatchCommand({
          type: "thread.turn.start",
          commandId: newCommandId(),
          threadId: threadIdForSend,
          message: {
            messageId: messageIdForSend,
            role: "user",
            text: outgoingMessageText,
            attachments: [],
          },
          modelSelection: selectedModelSelection,
          titleSeed: input.activeThread.title,
          runtimeMode: input.runtimeMode,
          interactionMode: nextInteractionMode,
          ...(nextInteractionMode === "default" && input.activeProposedPlan
            ? {
                sourceProposedPlan: {
                  threadId: input.activeThread.id,
                  planId: input.activeProposedPlan.id,
                },
              }
            : {}),
          createdAt: messageCreatedAt,
        });

        if (nextInteractionMode === "default") {
          await input.openPlanPanel(input.routeThreadId, input.environmentId);
        }
        input.sendInFlightRef.current = false;
      } catch (err) {
        input.setOptimisticUserMessages((existing) =>
          existing.filter((message) => message.id !== messageIdForSend),
        );
        input.setThreadError(
          threadIdForSend,
          err instanceof Error ? err.message : "Failed to send plan follow-up.",
        );
        input.sendInFlightRef.current = false;
        input.resetLocalDispatch();
      }
    },
    [input],
  );

  const onImplementPlanInNewThread = useCallback(async () => {
    const api = readEnvironmentApi(input.environmentId);
    if (
      !api ||
      !input.activeThread ||
      !input.activeProject ||
      !input.activeProposedPlan ||
      !input.isServerThread ||
      input.isSendBusy ||
      input.isConnecting ||
      input.sendInFlightRef.current
    ) {
      return;
    }

    const activeThread = input.activeThread;
    const activeProject = input.activeProject;
    const activeProposedPlan = input.activeProposedPlan;
    const {
      provider: selectedProvider,
      model: selectedModel,
      models: selectedProviderModels,
      promptEffort: selectedPromptEffort,
      modelSelection: selectedModelSelection,
    } = input.composerModelSelection;

    const createdAt = new Date().toISOString();
    const nextThreadId = newThreadId();
    const planMarkdown = activeProposedPlan.planMarkdown;
    const implementationPrompt = buildPlanImplementationPrompt(planMarkdown);
    const outgoingImplementationPrompt = formatOutgoingPrompt({
      provider: selectedProvider,
      model: selectedModel,
      models: selectedProviderModels,
      effort: selectedPromptEffort,
      text: implementationPrompt,
    });
    const nextThreadTitle = truncate(buildPlanImplementationThreadTitle(planMarkdown));

    input.sendInFlightRef.current = true;
    input.beginLocalDispatch({ preparingWorktree: false });
    const finish = () => {
      input.sendInFlightRef.current = false;
      input.resetLocalDispatch();
    };

    await api.orchestration
      .dispatchCommand({
        type: "thread.create",
        commandId: newCommandId(),
        threadId: nextThreadId,
        projectId: activeProject.id,
        title: nextThreadTitle,
        modelSelection: selectedModelSelection,
        runtimeMode: input.runtimeMode,
        interactionMode: "default",
        branch: input.activeThreadBranch,
        worktreePath: activeThread.worktreePath,
        createdAt,
      })
      .then(() => {
        return api.orchestration.dispatchCommand({
          type: "thread.turn.start",
          commandId: newCommandId(),
          threadId: nextThreadId,
          message: {
            messageId: newMessageId(),
            role: "user",
            text: outgoingImplementationPrompt,
            attachments: [],
          },
          modelSelection: selectedModelSelection,
          titleSeed: nextThreadTitle,
          runtimeMode: input.runtimeMode,
          interactionMode: "default",
          sourceProposedPlan: {
            threadId: activeThread.id,
            planId: activeProposedPlan.id,
          },
          createdAt,
        });
      })
      .then(() => {
        return waitForStartedServerThread(scopeThreadRef(activeThread.environmentId, nextThreadId));
      })
      .then(() => {
        return input.openPlanPanel(nextThreadId, activeThread.environmentId);
      })
      .catch(async (err: unknown) => {
        await api.orchestration
          .dispatchCommand({
            type: "thread.delete",
            commandId: newCommandId(),
            threadId: nextThreadId,
          })
          .catch(() => undefined);
        toastManager.add({
          type: "error",
          title: "Could not start implementation thread",
          description:
            err instanceof Error ? err.message : "An error occurred while creating the new thread.",
        });
      })
      .then(finish, finish);
  }, [input]);

  return { onSubmitPlanFollowUp, onImplementPlanInNewThread };
}
